import { Component, ComponentInterface, State, Method, Element, Prop, QueueApi, Watch, Listen, Event, EventEmitter, Host, h } from '@stencil/core';
import { Gesture, Color, ComponentRef, ComponentProps } from '../../../interface';
import { createColorClasses } from '../../../utils/theme';
import { enterAnimationBottom, enterAnimationRight } from '../modal/animations/enter';
import { config } from '../../../global/config';
import { leaveAnimationBottom, leaveAnimationRight } from '../modal/animations/leave';
import { attachComponent } from '../../../utils/helpers';
import { ModalComponent } from './modal-interface';
@Component({
  tag: 'lar-modal',
  styleUrl: 'modal.scss',
  shadow: true
})

export class Modal implements ComponentInterface {
  @Element() el!: HTMLElement;

  // Reference to the user's provided modal content
  public presented = false;
  private gesture?: Gesture;
  private config = config;
  private components: ModalComponent[] = [];

  /**
   * The icon color to use from your application's color palette.
   * Default options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop() color?: Color;
   /**
    * The small icon color to use from your application's color palette.
    */
  @Prop() colorIconSmall?: Color;
   /**
    * The icon color to use from your application's color palette.
    */
  @Prop() colorHeaderInputs?: Color;
   /**
    * The color to use from your application's color palette for value
    */
  @Prop() colorValue?: Color;
   /**
    * Modal appearing animation if animations enabled
    * Default options are: `"bottom"`, `"right"`
    */
  @Prop() animation: ('bottom' | 'right') = 'right';
   /**
    * Modal title big icon
    */
  @Prop() icon?: string;
   /**
    * Modal header size
    */
  @Prop() headerSize?: ('small' | 'default') = 'default';

   /**
    * Modal corner small icon
    */
  @Prop() iconSmall?: string;
   /**
    * Modal subscript title
    */
  @Prop() subTitle?: string;
   /**
    * Modal superscript title
    */
  @Prop() supTitle?: string;
   /**
    * Moda main title
    */
  @Prop() mainTitle?: string;
   /**
    * Node corner value
    */
  @Prop() value?: string;
   /**
    * Value translation valriables
    */
  @Prop() valueTranslationValues?: any;
   /**
    * Modal z-index
    */
  @Prop() overlayIndex?: number;
   /**
    * If `true`, a backdrop will be displayed behind the modal.
    */
  @Prop() showBackdrop = true;
   /**
    * If `true`, the modal will be dismissed when the backdrop is clicked.
    */
  @Prop() backdropDismiss = true;
   /**
    * If `true`, the modal will be dismissed when swiped back.
    */
  @Prop({ mutable: true }) swipeGesture?: boolean;

  /**
   * Emitted before the modal has presented.
   */
  @Event({ eventName: 'larmodalwillpresent' }) willPresent!: EventEmitter;

  /**
   * Emitted after the modal has presented.
   */
  @Event({ eventName: 'larmodaldidpresent' }) didPresent!: EventEmitter;

  /**
   * Emitted before the modal has dismissed.
   */
  @Event({ eventName: 'larmodalwilldismiss' }) willDismiss!: EventEmitter;

  /**
   * Emitted after the modal has dismissed.
   */
  @Event({ eventName: 'larmodaldiddismiss' }) didDismiss!: EventEmitter;

  @Watch('swipeGesture')
   swipeHandlerChanged() {
     if (this.gesture) {
       this.gesture.enable(!!this.swipeGesture);
     }
   }
  async componentWillLoad() {
     // swipe not set and enabled by config.
     if (this.swipeGesture === undefined) {
       this.swipeGesture = this.config.getBoolean('swipeBack', true);
     }
     if (this.component) {
        this.components.push({
          component: this.component,
          componentProps: { ...this.componentProps, modal: this.el }
        });
     }
   }

  /**
   * The component to display inside of the modal.
   */
  @Prop() component?: ComponentRef;

  /**
   * The data to pass to the modal component.
   */
  @Prop() componentProps?: ComponentProps;

  /**
   * Get modal components
   */
  @Method()
  async getComponents() {
    return this.components;
  }
  /**
   * Get modal component. If index not provided default returned
   */
  @Method()
  async getComponent(index?: number): Promise<ModalComponent | null> {
    if (index) {
      return this.components[index] || null;
    } else if (this.component && this.components[0]) {
      return this.components[0];
    }
    return null;
  }

  @Listen('larbackdroptap')
  protected onBackdropTap() {
    if (this.backdropDismiss) {
      this.dismiss();
    }
  }
  /**
   * Attach additional component to modal
   */
  @Method()
  async attachComponent(component: ComponentRef, componentProps?: ComponentProps) {
    this.components.push({
      component,
      componentProps
    });
  }
  /**
   * Present the modal overlay after it has been created.
   */
  @Method()
   async present(): Promise<void> {
     if (this.presented) {
       return;
     }
     // Wait for component to render before querying shadow DOM
     await new Promise(resolve => requestAnimationFrame(resolve));
     const el = this.el.shadowRoot || this.el;
     const container = el.querySelector(`.lar-modal-components-slot`);
     if (!container) {
       throw new Error('container is undefined');
     }
     this.willPresent.emit();
     this.presented = true;
     for (const comp of this.components) {
       comp.element = await attachComponent(container, comp.component, comp.componentProps);
     }
     const animationBuilder = this.animation === 'bottom'? enterAnimationBottom : enterAnimationRight;
     const animation = await import('../../../utils/animation').then(mod => mod.create(animationBuilder, el));
    // add 'display' to :host element before we start animating.
    // first time animation controller element creation to DOM takes time and default values may show you flickering because of beforeStyles functions
    // do awoid flickering with 'beforeStyles' params we display host after controller is created.
     if (!this.config.getBoolean('animated', true)) {
      animation.duration(0);
    }
     await animation.playAsync();
     animation.destroy();

    // OSX safari scroll fix: https://stackoverflow.com/questions/28288710/cant-scroll-inside-a-div-after-applying-webkit-transform-in-safari
     const scrollContent = el.querySelector('#lar-modal-scroll') as any;
     if (scrollContent) {
      scrollContent.style.overflowY = 'hidden'
      setTimeout(() => {
        scrollContent.style.overflowY = 'auto'
      });
    }
     this.didPresent.emit();
   }

  async componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    const animationBuilder = this.animation === 'bottom'? leaveAnimationBottom : leaveAnimationRight;
    const dismissAnimation = await import('../../../utils/animation').then(mod => mod.create(animationBuilder, el));
    const swipe = this.animation === 'bottom'? await import('../../../utils/gesture/swipe-down') : await import('../../../utils/gesture/swipe-back');

    let shouldHide = false;
    dismissAnimation.onFinish(()=> {
      if (shouldHide) {
        this.willDismiss.emit();
        this.didDismiss.emit();
      }
    });

    this.gesture = swipe.createSwipeBackGesture(
    this.el,
    () => !!this.swipeGesture,
    () => {
        return dismissAnimation && dismissAnimation.progressStart();
    },
    step => {
      return dismissAnimation && dismissAnimation.progressStep(step);
    },
    async (shouldComplete, step, dur) => {
      if (dismissAnimation) {
        dismissAnimation.progressEnd(shouldComplete, step, dur);
      }
      shouldHide = shouldComplete;
    });

    this.swipeHandlerChanged();
  }

   /**
    * Present the modal overlay after it has been created.
    */
  @Method()
    async dismiss(): Promise<void> {
      if (this.presented) {
        this.willDismiss.emit();
        const el = this.el.shadowRoot || this.el;
        const animationBuilder = this.animation === 'bottom'? leaveAnimationBottom : leaveAnimationRight;
        const animation = await import('../../../utils/animation').then(mod => mod.create(animationBuilder, el));
        if (!this.config.getBoolean('animated', true)) {
          animation.duration(0);
        }
        await animation.playAsync();
        animation.destroy();
        this.didDismiss.emit();
      }
    }

  render() {
      // if it comes using DOM prop, objects not supporteed, so node.tsx coverts it to JSON string
      const valueTranslationValues = !!this.valueTranslationValues && typeof this.valueTranslationValues === 'string' ?
        JSON.parse(this.valueTranslationValues) :
        this.valueTranslationValues;
      let iconSmall: HTMLLarIconElement;
      let body: HTMLElement[];
      let value: HTMLLarTranslateElement;
      if (this.iconSmall) {
        iconSmall = <lar-icon size="large" color={this.colorIconSmall||this.colorHeaderInputs||this.color} icon={this.iconSmall} />;
      }
      if (this.value && this.value !== '') {
        value = <lar-translate t={this.value} values={valueTranslationValues} />;
      }
      body = [
        <div class="lar-modal-header">
          <div class="lar-toolbar">
            <div class="lar-toolbar-background"></div>
            <div class="lar-toolbar-container">
              <div class="lar-start">
                <a href="#" onClick={(e: UIEvent) => { e.preventDefault(); e.stopPropagation(); this.dismiss() }}>
                  <lar-icon size="medium" icon={this.animation === 'bottom'?'down':'back'} color={this.colorHeaderInputs||'medium'} />
                </a>
              </div>
              <div class="lar-mid">
                <div class={'lar-modal-icon ' + this.headerSize}>
                    {this.icon
                      ? <lar-icon icon={this.icon} />
                      : ''
                    }
                </div>
              </div>
              <div class="lar-end">
                {iconSmall}
              </div>
            </div>
          </div>
        </div>,
        <div class="lar-modal-content">
          <div id="lar-modal-scroll" class={'lar-modal-scroll ' + this.headerSize}>
            <div class="lar-modal-body">
              <h3><lar-translate t={this.supTitle} /></h3>
              <h2><lar-translate t={this.mainTitle} /></h2>
              <h3><lar-translate t={this.subTitle} /></h3>
              <div class="lar-value">{value}</div>
              <slot name="lar-modal-components-slot"><div class="lar-modal-components-slot" /></slot>
            </div>
          </div>
        </div>
      ];

      return (
        <Host
          class={{ ...createColorClasses(this.color), 'lar-modal': true }}
          style={{
            'zIndex': String(200 + (this.overlayIndex || 0)),
          }}
        >
          <lar-backdrop visible={this.showBackdrop} tappable={this.backdropDismiss}/>
          <div role="dialog" class="lar-modal-wrapper">
            {body}
          </div>
        </Host>
      );
    }
}
